import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import NextImage from '$/components/NextImage';
import Waterfall from '@/components/Waterfall';
import styles from './index.module.scss';

const DEFAULT_COVER_ASPECT_RATIO = 4 / 5;
const CARD_HORIZONTAL_PADDING = 16;
const CARD_CONTENT_HEIGHT = 92;
const MAX_VISIBLE_CAST_IPS = 5;

const GALLERY_BREAKPOINT_COLUMNS = {
  1232: 5,
  984: 4,
  736: 3,
  488: 2,
  0: 1,
};

export interface FeaturedCaseGalleryAuthor {
  /** 邀约创作者时使用 case.user.uid，禁止从出演 IP 推断。 */
  uid?: string;
  name: string;
  avatarUrl?: string;
}

export interface FeaturedCaseGalleryCastIp {
  id: string;
  assetType?: 'official' | 'personal';
  roleName?: string;
  name: string;
  summary?: string;
  coverAssetId?: string;
  avatarUrl?: string;
}

export type FeaturedCaseIpDisplayMode = 'official' | 'invite_creator' | 'legacy';

export interface FeaturedCaseGalleryItem {
  /** 对应接口 featured_id；请求层保留大整数精度，适配层统一转换为字符串。 */
  featuredId: string;
  caseId?: string;
  roomId?: string;
  projectId?: string;
  /** 打开详情或预览时与 featured_id 一并传入，保留来源投放上下文。 */
  placement: 'website_home' | 'workspace_home' | 'app_tab';
  categoryId?: string;
  region?: 'cn' | 'overseas';
  locale?: string;
  title: string;
  coverUrl: string;
  coverAlt?: string;
  coverWidth?: number;
  coverHeight?: number;
  previewVideoUrl?: string;
  /** 设计侧指定的整卡高度；未传时按封面宽高比自然撑开。 */
  cardHeight?: number;
  author: FeaturedCaseGalleryAuthor;
  /** legacy 案例不展示出演 IP；其余模式按数组顺序展示。 */
  castIps?: FeaturedCaseGalleryCastIp[];
  /** 出演 IP 总数可大于 castIps 长度，用于服务端仅返回部分头像时展示 +N。 */
  totalCastIpCount?: number;
  copyAllowed?: boolean;
  workflowPublic?: boolean;
  ipDisplayMode?: FeaturedCaseIpDisplayMode;
  copyDisabledReason?: string;
}

export interface FeaturedCaseGalleryLabels {
  createSame: string;
  formatCastIpCount: (count: number) => React.ReactNode;
}

export interface FeaturedCaseGalleryProps {
  items: FeaturedCaseGalleryItem[];
  title: React.ReactNode;
  labels: FeaturedCaseGalleryLabels;
  className?: string;
  /** 嵌入首页内容区时由外层提供标题和留白，展示全部卡片而不裁切。 */
  embedded?: boolean;
  /** 创建期间禁用列表内的同款按钮，避免并发创建。 */
  creatingFeaturedId?: string;
  onCaseClick?: (item: FeaturedCaseGalleryItem, index: number) => void;
  onCaseExposed?: (item: FeaturedCaseGalleryItem, index: number) => void;
  onCastIpClick?: (
    castIp: FeaturedCaseGalleryCastIp,
    item: FeaturedCaseGalleryItem,
    index: number,
  ) => void;
  onCreateSameClick?: (item: FeaturedCaseGalleryItem, index: number) => void;
}

interface FeaturedCaseCardProps {
  item: FeaturedCaseGalleryItem;
  index: number;
  labels: FeaturedCaseGalleryLabels;
  creatingFeaturedId?: string;
  onCaseClick?: FeaturedCaseGalleryProps['onCaseClick'];
  onCaseExposed?: FeaturedCaseGalleryProps['onCaseExposed'];
  onCastIpClick?: FeaturedCaseGalleryProps['onCastIpClick'];
  onCreateSameClick?: FeaturedCaseGalleryProps['onCreateSameClick'];
}

interface CastIpAvatarProps {
  castIp: FeaturedCaseGalleryCastIp;
  item: FeaturedCaseGalleryItem;
  itemIndex: number;
  priority: boolean;
  onCaseClick?: FeaturedCaseGalleryProps['onCaseClick'];
  onCastIpClick?: FeaturedCaseGalleryProps['onCastIpClick'];
}

/** 根据封面尺寸计算展示比例，异常尺寸统一回退为 4:5。 */
function getCoverAspectRatio(item: FeaturedCaseGalleryItem) {
  if (item.coverWidth && item.coverHeight && item.coverWidth > 0 && item.coverHeight > 0) {
    return item.coverWidth / item.coverHeight;
  }
  return DEFAULT_COVER_ASPECT_RATIO;
}

/** 为瀑布流首屏分列提供稳定的卡片预估高度。 */
function getEstimatedCardHeight(item: FeaturedCaseGalleryItem, _index: number, itemWidth: number) {
  if (item.cardHeight && item.cardHeight > 0) {
    return item.cardHeight;
  }
  const coverWidth = Math.max(itemWidth - CARD_HORIZONTAL_PADDING, 0);
  // 无出演行时同时扣除行高 36px 和卡片间距 8px，避免瀑布流首屏预估留下空位。
  const contentHeight = getVisibleCastIps(item).length ? CARD_CONTENT_HEIGHT : CARD_CONTENT_HEIGHT - 44;
  return coverWidth / getCoverAspectRatio(item) + contentHeight;
}

/** 返回案例的稳定业务标识，供瀑布流缓存实测高度。 */
function getCaseKey(item: FeaturedCaseGalleryItem) {
  return item.featuredId;
}

/** 公开工作流即可进入预览，不与复制保存权限混用。 */
function canCreateSame(item: FeaturedCaseGalleryItem) {
  return item.workflowPublic === true;
}

/** 读取设计允许展示的出演 IP，并确保 legacy 案例没有出演 IP 槽。 */
function getVisibleCastIps(item: FeaturedCaseGalleryItem) {
  if (item.ipDisplayMode !== 'official') {
    return [];
  }
  return (item.castIps ?? []).slice(0, MAX_VISIBLE_CAST_IPS);
}

/** 渲染带角色名悬浮提示的出演 IP 头像。 */
function CastIpAvatar({
  castIp,
  item,
  itemIndex,
  priority,
  onCaseClick,
  onCastIpClick,
}: CastIpAvatarProps) {
  const hasAvatarAction = Boolean(onCastIpClick || onCaseClick);

  /** IP 有独立入口时执行跳转，否则保持整卡点击行为。 */
  const handleCastIpClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onCastIpClick) {
      onCastIpClick(castIp, item, itemIndex);
      return;
    }
    onCaseClick?.(item, itemIndex);
  }, [castIp, item, itemIndex, onCaseClick, onCastIpClick]);

  const avatar = castIp.avatarUrl ? (
    <NextImage
      className={styles.avatar}
      src={castIp.avatarUrl}
      alt=""
      width={30}
      height={30}
      sizes="30px"
      priority={priority}
      useOriginalImage
      preserveSourceFormat
    />
  ) : (
    <span className={styles.avatarPlaceholder} aria-hidden />
  );

  return (
    <span className={styles.castIpAvatarItem}>
      {hasAvatarAction ? (
        <button
          type="button"
          className={styles.castIpAvatarButton}
          aria-label={castIp.name}
          onClick={handleCastIpClick}
        >
          {avatar}
        </button>
      ) : avatar}
      <span className={styles.castIpTooltip} role="tooltip">{castIp.name}</span>
    </span>
  );
}

const MemoizedCastIpAvatar = memo(CastIpAvatar);

/** 作者信息仅用于封面悬浮层，不作为底部出演 IP 的兜底。 */
function AuthorIdentity({ author }: { author: FeaturedCaseGalleryAuthor; }) {
  return (
    <span className={styles.identity}>
      {author.avatarUrl ? (
        <NextImage
          className={styles.avatar}
          src={author.avatarUrl}
          alt=""
          width={30}
          height={30}
          sizes="30px"
        />
      ) : (
        <span className={styles.avatarPlaceholder} aria-hidden />
      )}
      <span className={styles.identityName} title={author.name}>{author.name}</span>
    </span>
  );
}

/** 渲染单个精选案例卡片的默认、悬浮、单 IP 与多 IP 状态。 */
function FeaturedCaseCard({
  item,
  index,
  labels,
  creatingFeaturedId,
  onCaseClick,
  onCaseExposed,
  onCastIpClick,
  onCreateSameClick,
}: FeaturedCaseCardProps) {
  const cardRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const exposed = useRef(false);
  const hasFixedHeight = Boolean(item.cardHeight && item.cardHeight > 0);
  const showCreateSame = Boolean(onCreateSameClick) && canCreateSame(item);
  const visibleCastIps = getVisibleCastIps(item);
  const hasCastIps = visibleCastIps.length > 0;
  // 接口适配项始终传入 casting_ip_count；数组长度仅兼容纯组件调用方。
  const totalCastIpCount = Math.max(item.totalCastIpCount ?? item.castIps?.length ?? 0, 0);
  const overflowCount = Math.max(totalCastIpCount - visibleCastIps.length, 0);
  const overflowLabel = `+${overflowCount}`;
  const cardStyle = hasFixedHeight ? { height: item.cardHeight } : undefined;
  const coverStyle = hasFixedHeight ? undefined : { aspectRatio: getCoverAspectRatio(item) };

  /** 播放静音预览；浏览器拒绝自动播放时保持封面图。 */
  const playPreviewVideo = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.play().catch(() => undefined);
  }, []);

  /** 离开卡片后暂停并复位视频，下次悬浮从头开始。 */
  const stopPreviewVideo = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, []);

  /** 在整张卡片的 hover 生命周期内控制视频，避免进入子按钮时意外暂停。 */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return undefined;
    card.addEventListener('mouseenter', playPreviewVideo);
    card.addEventListener('mouseleave', stopPreviewVideo);
    return () => {
      card.removeEventListener('mouseenter', playPreviewVideo);
      card.removeEventListener('mouseleave', stopPreviewVideo);
    };
  }, [playPreviewVideo, stopPreviewVideo]);

  /** 卡片至少一半进入视口才计入曝光，同一挂载实例只统计一次。 */
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !onCaseExposed || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || entry.intersectionRatio < 0.5 || exposed.current) return;
      exposed.current = true;
      onCaseExposed(item, index);
      observer.disconnect();
    }, { threshold: 0.5 });
    observer.observe(card);
    return () => observer.disconnect();
  }, [index, item, onCaseExposed]);

  /** 点击卡片主体时打开对应 featured_id 的案例详情。 */
  const handleCaseClick = useCallback(() => {
    onCaseClick?.(item, index);
  }, [index, item, onCaseClick]);

  /** 阻断操作按钮事件向外冒泡并触发创作同款回调。 */
  const handleCreateSameClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCreateSameClick?.(item, index);
  }, [index, item, onCreateSameClick]);

  return (
    <article
      ref={cardRef}
      className={classNames(styles.caseCard, {
        [styles.fixedHeight]: hasFixedHeight,
      })}
      style={cardStyle}
    >
      {onCaseClick && (
        <button
          type="button"
          className={styles.caseClickTarget}
          aria-label={item.title}
          onClick={handleCaseClick}
          onFocus={playPreviewVideo}
          onBlur={stopPreviewVideo}
        />
      )}
      <span className={styles.coverWrap} style={coverStyle}>
        <NextImage
          className={styles.cover}
          src={item.coverUrl}
          alt={item.coverAlt ?? item.title}
          width={640}
          sizes="(max-width: 487px) calc(100vw - 32px), (max-width: 735px) 50vw, (max-width: 983px) 33vw, (max-width: 1231px) 25vw, 300px"
          priority={index < 5}
        />
        {item.previewVideoUrl && (
          <video
            ref={videoRef}
            className={styles.previewVideo}
            crossOrigin="anonymous"
            src={item.previewVideoUrl}
            muted
            loop
            playsInline
            preload="none"
          />
        )}
        <span className={styles.coverMeta}>
          <AuthorIdentity author={item.author} />
          {showCreateSame && (
            <button
              type="button"
              className={styles.createSameButton}
              disabled={Boolean(creatingFeaturedId)}
              aria-busy={creatingFeaturedId === item.featuredId}
              onClick={handleCreateSameClick}
            >
              <span>{labels.createSame}</span>
              <IconFont name="chevronRightBlack" fontSize={11} />
            </button>
          )}
        </span>
      </span>

      <span className={styles.caseTitle} title={item.title}>{item.title}</span>

      {hasCastIps && (
        <span className={styles.metadataRow}>
          <span className={styles.castIpAvatars}>
            {visibleCastIps.map((castIp) => (
              <MemoizedCastIpAvatar
                key={castIp.id}
                castIp={castIp}
                item={item}
                itemIndex={index}
                priority={index < 5}
                onCaseClick={onCaseClick}
                onCastIpClick={onCastIpClick}
              />
            ))}
            {overflowCount > 0 && (
            <span className={classNames(styles.avatar, styles.overflowAvatar)}>
              {overflowLabel}
            </span>
            )}
          </span>
          <span className={styles.metadataText}>
            {totalCastIpCount > 1
              ? labels.formatCastIpCount(totalCastIpCount)
              : visibleCastIps[0].name}
          </span>
        </span>
      )}
    </article>
  );
}

const MemoizedFeaturedCaseCard = memo(FeaturedCaseCard);

/** 渲染精选案例标题及满足 240–320px 卡宽规则的响应式瀑布流。 */
function FeaturedCaseGallery({
  items,
  title,
  labels,
  className,
  embedded = false,
  creatingFeaturedId,
  onCaseClick,
  onCaseExposed,
  onCastIpClick,
  onCreateSameClick,
}: FeaturedCaseGalleryProps) {
  /** 为瀑布流渲染案例卡片，并透传外部交互。 */
  const renderCase = useCallback((item: FeaturedCaseGalleryItem, index: number) => (
    <MemoizedFeaturedCaseCard
      item={item}
      index={index}
      labels={labels}
      creatingFeaturedId={creatingFeaturedId}
      onCaseClick={onCaseClick}
      onCaseExposed={onCaseExposed}
      onCastIpClick={onCastIpClick}
      onCreateSameClick={onCreateSameClick}
    />
  ), [labels, creatingFeaturedId, onCaseClick, onCaseExposed, onCastIpClick, onCreateSameClick]);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className={classNames(styles.gallery, { [styles.embedded]: embedded }, className)}>
      {title && <h2 className={styles.heading}>{title}</h2>}
      <div className={styles.viewport}>
        <Waterfall
          className={styles.waterfall}
          items={items}
          renderItem={renderCase}
          getItemKey={getCaseKey}
          getEstimatedHeight={getEstimatedCardHeight}
          minColumnWidth={240}
          maxColumnWidth={320}
          columnGap={8}
          rowGap={8}
          breakpoints={GALLERY_BREAKPOINT_COLUMNS}
        />
        {!embedded && <div className={styles.bottomFade} aria-hidden />}
      </div>
    </section>
  );
}

export default memo(FeaturedCaseGallery);
